import React, { useState, useEffect } from 'react';
import { X, Plus, FileText, EyeOff, Layout } from 'lucide-react';
import { cn } from '../utils/cn';
import { TemplateLibraryModal } from './TemplateLibraryModal';
import { layoutsApi } from '../api/client';
import type { PageTemplate, PageLayout } from '../schemas/validation';

// Special sentinel values for the layout dropdown
const LAYOUT_SITE_DEFAULT = '__site_default__';
const LAYOUT_NONE = '__none__';

interface CreatePageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    title: string,
    slug: string,
    isHomePage?: boolean,
    noindex?: boolean,
    layoutId?: string | null,
    showLayout?: boolean,
    addToNav?: boolean,
    addToFooter?: boolean
  ) => void;
  onSubmitFromTemplate?: (
    title: string,
    slug: string,
    templateId: string,
    isHomePage?: boolean,
    noindex?: boolean,
    layoutId?: string | null,
    showLayout?: boolean,
    addToNav?: boolean,
    addToFooter?: boolean
  ) => void;
  isLoading?: boolean;
  siteId?: string;
}

export const CreatePageModal: React.FC<CreatePageModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSubmitFromTemplate,
  isLoading = false,
  siteId,
}) => {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [isHomePage, setIsHomePage] = useState(false);
  const [noindex, setNoindex] = useState(false);
  const [addToNav, setAddToNav] = useState(false);
  const [addToFooter, setAddToFooter] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PageTemplate | null>(null);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);

  // Layout state
  const [layouts, setLayouts] = useState<PageLayout[]>([]);
  const [isLoadingLayouts, setIsLoadingLayouts] = useState(false);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null);
  const [showLayout, setShowLayout] = useState(true);
  const [selectedLayoutValue, setSelectedLayoutValue] = useState(LAYOUT_SITE_DEFAULT);

  // Auto-generate slug from title
  useEffect(() => {
    if (title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      setSlug(generatedSlug);
    }
  }, [title]);

  // Load layouts when modal opens
  useEffect(() => {
    const loadLayouts = async () => {
      if (!siteId) return;

      try {
        setIsLoadingLayouts(true);
        const response = await layoutsApi.list(siteId);
        setLayouts(response.layouts);

        // Pre-select "Site Default" (layoutId null, showLayout true)
        setSelectedLayoutValue(LAYOUT_SITE_DEFAULT);
        setSelectedLayoutId(null);
        setShowLayout(true);
      } catch (error) {
        console.error('Failed to load layouts:', error);
        setLayouts([]);
      } finally {
        setIsLoadingLayouts(false);
      }
    };

    if (isOpen && siteId) {
      loadLayouts();
    }
  }, [isOpen, siteId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setSlug('');
      setIsHomePage(false);
      setNoindex(false);
      setAddToNav(false);
      setAddToFooter(false);
      setErrors({});
      setUseTemplate(false);
      setSelectedTemplate(null);
      // Reset to site default
      setSelectedLayoutValue(LAYOUT_SITE_DEFAULT);
      setSelectedLayoutId(null);
      setShowLayout(true);
    } else {
      // Reset layout state when modal closes
      setSelectedLayoutId(null);
      setSelectedLayoutValue(LAYOUT_SITE_DEFAULT);
      setLayouts([]);
    }
  }, [isOpen]);

  // Handle layout dropdown change
  const handleLayoutChange = (value: string) => {
    setSelectedLayoutValue(value);
    if (value === LAYOUT_NONE) {
      setSelectedLayoutId(null);
      setShowLayout(false);
      setAddToNav(false);
      setAddToFooter(false);
    } else if (value === LAYOUT_SITE_DEFAULT) {
      setSelectedLayoutId(null);
      setShowLayout(true);
    } else {
      // Specific layout UUID
      setSelectedLayoutId(value);
      setShowLayout(true);
    }
  };

  const defaultLayout = layouts.find(l => l.isDefault);

  // Update title when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      const templateTitle = selectedTemplate.pageData.title || selectedTemplate.name;
      setTitle(templateTitle);
    }
  }, [selectedTemplate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Page title is required';
    }

    if (!slug.trim()) {
      newErrors.slug = 'Page slug is required';
    } else if (!/^[a-z0-9-]*$/.test(slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (useTemplate && !selectedTemplate) {
      newErrors.template = 'Please select a template';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (useTemplate && selectedTemplate && onSubmitFromTemplate) {
      onSubmitFromTemplate(
        title.trim(),
        slug.trim(),
        selectedTemplate.id,
        isHomePage,
        noindex,
        selectedLayoutId,
        showLayout,
        showLayout && addToNav,
        showLayout && addToFooter
      );
    } else {
      onSubmit(
        title.trim(),
        slug.trim(),
        isHomePage,
        noindex,
        selectedLayoutId,
        showLayout,
        showLayout && addToNav,
        showLayout && addToFooter
      );
    }
  };

  const handleTemplateSelect = (template: PageTemplate) => {
    setSelectedTemplate(template);
    setUseTemplate(true);
    setShowTemplateLibrary(false);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={cn(
          'modal-backdrop',
          'flex items-center justify-center z-50 p-4',
          isOpen ? 'animate-fade-in' : ''
        )}
        onClick={handleBackdropClick}
      >
        <div
          className={cn(
            'modal-content',
            'max-w-md w-full max-h-screen overflow-y-auto',
            isOpen ? 'animate-slide-up' : ''
          )}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create New Page</h2>
              <p className="text-sm text-gray-500 mt-1">Add a new page to your website</p>
            </div>
            <button
              onClick={onClose}
              className={cn(
                'p-2 text-gray-400 hover:text-gray-600 rounded-lg',
                'hover:bg-gray-100 transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              )}
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="space-y-3">
              {/* Template Selection Options */}
              {onSubmitFromTemplate && (
                <div>
                  <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => {
                        setUseTemplate(false);
                        setSelectedTemplate(null);
                      }}
                      className={cn(
                        'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2',
                        !useTemplate
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      )}
                      disabled={isLoading}
                    >
                      <Plus size={16} />
                      <span>From Scratch</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTemplateLibrary(true)}
                      className={cn(
                        'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2',
                        useTemplate
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      )}
                      disabled={isLoading}
                    >
                      <FileText size={16} />
                      <span>From Template</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Template Selection */}
              {useTemplate && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Template <span className="text-red-500">*</span>
                  </label>
                  {selectedTemplate ? (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{selectedTemplate.name}</h4>
                          {selectedTemplate.description && (
                            <p className="text-sm text-gray-600 mt-1">
                              {selectedTemplate.description}
                            </p>
                          )}
                          <div className="flex items-center text-xs text-gray-500 mt-2">
                            <span>{selectedTemplate.usageCount} uses</span>
                            {selectedTemplate.isPublic && (
                              <>
                                <span className="mx-2">•</span>
                                <span>Public template</span>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowTemplateLibrary(true)}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          disabled={isLoading}
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowTemplateLibrary(true)}
                      className={cn(
                        'w-full p-4 border-2 border-dashed border-gray-300 rounded-lg',
                        'hover:border-primary-400 hover:bg-primary-50 transition-colors',
                        'flex items-center justify-center space-x-2 text-gray-600 hover:text-primary-600'
                      )}
                      disabled={isLoading}
                    >
                      <Plus size={20} />
                      <span>Choose Template</span>
                    </button>
                  )}
                  {errors.template && (
                    <p className="form-error">
                      <span>{errors.template}</span>
                    </p>
                  )}
                </div>
              )}

              <div>
                <label
                  htmlFor="pageTitle"
                  className="block text-sm font-semibold text-gray-700 mb-1"
                >
                  Page Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="pageTitle"
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className={cn(
                    'input-field',
                    errors.title && 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  )}
                  placeholder="e.g., About Us, Contact, Services"
                  disabled={isLoading}
                  autoFocus={!useTemplate}
                />
                {errors.title && (
                  <p className="form-error">
                    <span>{errors.title}</span>
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="pageSlug"
                  className="block text-sm font-semibold text-gray-700 mb-1"
                >
                  Page URL <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md px-3 py-2">
                    /
                  </span>
                  <input
                    id="pageSlug"
                    type="text"
                    value={slug}
                    onChange={e => setSlug(e.target.value)}
                    className={cn(
                      'input-field rounded-l-none',
                      errors.slug && 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    )}
                    placeholder="about-us, contact, services"
                    disabled={isLoading}
                  />
                </div>
                {errors.slug && (
                  <p className="form-error">
                    <span>{errors.slug}</span>
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  This will be the web address for your page. Use lowercase letters, numbers, and
                  hyphens only.
                </p>
              </div>

              {/* Home Page Checkbox */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <input
                    id="isHomePage"
                    type="checkbox"
                    checked={isHomePage}
                    onChange={e => setIsHomePage(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="isHomePage" className="block text-sm font-medium text-gray-700">
                    Set as Home Page
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    This page will be the default page visitors see when they visit your website.
                  </p>
                </div>
              </div>

              {/* Hide from Search Engines Checkbox */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <input
                    id="noindex"
                    type="checkbox"
                    checked={noindex}
                    onChange={e => setNoindex(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={isLoading}
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="noindex" className="block text-sm font-medium text-gray-700">
                    <span className="flex items-center space-x-2">
                      <EyeOff size={14} />
                      <span>Hide from Search Engines</span>
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    When enabled, this page will be excluded from the sitemap and search engines
                    will be asked not to index it.
                  </p>
                </div>
              </div>

              {/* Include in Main Navigation */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <input
                    id="addToNav"
                    type="checkbox"
                    checked={addToNav}
                    onChange={e => setAddToNav(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={isLoading || !showLayout}
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="addToNav" className="block text-sm font-medium text-gray-700">
                    Include in Main Navigation
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    {showLayout
                      ? 'Adds this page to the header menu.'
                      : 'Nav and footer come from the page layout.'}
                  </p>
                </div>
              </div>

              {/* Add link to Footer */}
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <input
                    id="addToFooter"
                    type="checkbox"
                    checked={addToFooter}
                    onChange={e => setAddToFooter(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    disabled={isLoading || !showLayout}
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="addToFooter" className="block text-sm font-medium text-gray-700">
                    Add link to page in Footer
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    {showLayout
                      ? 'Adds this page to the footer links.'
                      : 'Nav and footer come from the page layout.'}
                  </p>
                </div>
              </div>

              {/* Page Layout Selection */}
              {siteId && (
                <div className="pt-3 border-t border-gray-100">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    <span className="flex items-center space-x-2">
                      <Layout size={14} />
                      <span>Page Layout</span>
                    </span>
                  </label>
                  {isLoadingLayouts ? (
                    <div className="flex items-center space-x-2 text-gray-500 text-sm">
                      <div className="w-4 h-4 spinner"></div>
                      <span>Loading layouts...</span>
                    </div>
                  ) : layouts.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No layouts available. You can add a layout later in page settings.
                    </p>
                  ) : (
                    <>
                      <select
                        id="layoutId"
                        value={selectedLayoutValue}
                        onChange={e => handleLayoutChange(e.target.value)}
                        disabled={isLoading}
                        className={cn('input-field', 'w-full')}
                      >
                        <option value={LAYOUT_SITE_DEFAULT}>
                          Site Default{defaultLayout ? ` (${defaultLayout.name})` : ' (none set)'}
                        </option>
                        <option value={LAYOUT_NONE}>No Layout (Standalone / Landing Page)</option>
                        {layouts.map(layout => (
                          <option key={layout.id} value={layout.id}>
                            {layout.name}
                          </option>
                        ))}
                      </select>

                      <p className="text-xs text-gray-500 mt-2">
                        {selectedLayoutValue === LAYOUT_SITE_DEFAULT
                          ? defaultLayout
                            ? `Uses your site's default layout: "${defaultLayout.name}" — includes header & footer.`
                            : 'Uses the site default layout. No default layout is currently set — page will have no header or footer until one is created.'
                          : selectedLayoutValue === LAYOUT_NONE
                            ? 'No header or footer. Ideal for landing pages.'
                            : 'Uses a specific layout for this page.'}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={cn('btn-primary', isLoading && 'opacity-50 cursor-not-allowed')}
                disabled={isLoading || !title.trim() || (useTemplate && !selectedTemplate)}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 spinner"></div>
                    <span>Creating...</span>
                  </div>
                ) : useTemplate ? (
                  'Create from Template'
                ) : (
                  'Create Page'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Template Library Modal */}
      <TemplateLibraryModal
        isOpen={showTemplateLibrary}
        onClose={() => setShowTemplateLibrary(false)}
        onSelectTemplate={handleTemplateSelect}
        siteId={siteId}
      />
    </>
  );
};
